import { yupResolver } from "@hookform/resolvers/yup";
import { IonCol, IonGrid, IonRow } from "@ionic/react";
import { SetStateAction, useCallback, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import * as yup from "yup";

import Button from "@components/buttons/Button";
import CircleButton from "@components/buttons/CircleButton";
import BaseErrors from "@components/fields/BaseErrors";
import CheckboxGroup from "@components/fields/CheckboxGroup";
import FormButtons from "@components/fields/FormButtons";
import Input from "@components/fields/Input";
import Form from "@components/forms/Form";
import styles from "@components/forms/InviteAccountMemberForm.module.css";
import IconBase from "@components/text/IconBase";
import BodyCopy from "@components/typography/BodyCopy";
import useCareTeamInviteCreateMutation from "@hooks/useCareTeamInviteCreateMutation";
import { BackOrNextEnum } from "@typing/Enums";
import { CareTeamInviteCreateMutation } from "@typing/Generated";
import { JourneyFromSomeQuery } from "@typing/Models";
import { handleMutationResponse } from "@utils/formUtils";

type CareTeamInviteCreatePayload = NonNullable<CareTeamInviteCreateMutation["careTeamInviteCreate"]>;

type FormData = {
  emails: { email: string | null }[];
  invitedClientIds?: string[] | undefined;
  message: string | null;
};

type Props = {
  autofocus: boolean;
  buttonExpand?: "block" | "full";
  consultScheduling?:
    | {
        inviteOptions: {
          label: string;
          value: string;
        }[];
        selectedClientIds: string[];
      }
    | undefined;
  journey: JourneyFromSomeQuery;
  onCancel?: () => void;
  onSuccess?: (inviteEmails?: string[], invitedClientIds?: string[]) => void;
  saveKey?: string;
  setBackOrNext?: (value: SetStateAction<BackOrNextEnum | null>) => void;
  skipCancel?: boolean;
};

const InviteAccountMembersForm = ({
  buttonExpand,
  consultScheduling,
  journey,
  onCancel,
  onSuccess,
  saveKey = "dictionary.send",
  skipCancel
}: Props) => {
  const careTeamInviteCreate = useCareTeamInviteCreateMutation(journey);
  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      emails: yup
        .array()
        .defined()
        .of(
          yup.object().shape({
            email: yup
              .string()
              .defined()
              .nullable()
              .email(intl.formatMessage({ id: "errors.inviteAccountMembers.emailFormat" }))
          })
        )
        .min(1, intl.formatMessage({ id: "errors.inviteAccountMembers.moreEmails" })),
      invitedClientIds: yup.array().defined().of(yup.string().defined()),
      message: yup.string().nullable().defined()
    })
    .required();

  const defaultValues: FormData = {
    emails: [{ email: null }, { email: null }, { email: null }],
    invitedClientIds: consultScheduling?.selectedClientIds ?? [],
    message: null
  };

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const {
    append: emailAppend,
    fields: emailFields,
    remove: emailRemove
  } = useFieldArray<FormData>({
    control: form.control,
    name: "emails"
  });

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);

    const emails = form.getValues().emails.map(e => e.email);

    // filter out empty values, duplicates, and invites that are already added
    const emailAddresses = emails
      .filter(email => email !== undefined && email !== null)
      .filter((email, index, array) => array.indexOf(email) === index)
      .filter(
        email =>
          email &&
          !journey.careTeamInvites?.some(invite => invite.email === email && !invite.canceled && !invite.accepted)
      ) as string[];

    if (consultScheduling) return onSuccess?.(emailAddresses, form.getValues().invitedClientIds);

    await Promise.all(
      emailAddresses.map(async (email, index) =>
        careTeamInviteCreate({
          variables: {
            email,
            journeyId: journey.id,
            message: form.getValues().message
          }
        }).then(response => {
          if (response.data?.careTeamInviteCreate?.errors.length) {
            response.data.careTeamInviteCreate.errors.forEach(fieldError => {
              const messages = fieldError.errors.map(error =>
                intl.formatMessage({ id: `errors.inviteAccountMembers.${fieldError.field}.${error}` })
              );

              if (fieldError.field === "base") {
                setBaseErrors(messages);
              } else {
                const field = `emails.${index}.${fieldError.field}`;
                form.setError(field as keyof FormData, { message: messages.join(" ") });
              }
            });
          }
          return response;
        })
      )
    ).then(response => {
      const invites = response
        .filter(invite => invite.data?.careTeamInviteCreate)
        .map(invite => invite.data?.careTeamInviteCreate) as CareTeamInviteCreatePayload[];
      handleMutationResponse<FormData>({
        data: invites,
        intl,
        onSuccess: () => {
          form.reset();
          onSuccess?.(
            invites.filter(value => value.careTeamInvite?.id).map(invite => invite.careTeamInvite?.id) as string[],
            form.getValues().invitedClientIds
          );
        },
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.inviteAccountMembers"
      });
    });
  }, [careTeamInviteCreate, consultScheduling, form, intl, journey.careTeamInvites, journey.id, onSuccess]);

  return (
    <Form data-test="invite-form" hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />

      {consultScheduling?.inviteOptions && consultScheduling.inviteOptions.length > 0 && (
        <div className="ion-margin-bottom">
          <div className="ion-margin-bottom">
            <BodyCopy>
              <FormattedMessage id="forms.consult.inviteClients" />
            </BodyCopy>
          </div>
          <CheckboxGroup<FormData>
            control={form.control}
            errors={form.formState.errors.invitedClientIds}
            label=""
            name="invitedClientIds"
            options={consultScheduling?.inviteOptions ?? []}
            required
          />
        </div>
      )}

      <div className="ion-margin-bottom">
        <BodyCopy>
          <FormattedMessage
            id={consultScheduling ? "forms.consult.invitedCareTeamInviteIdsSublabel" : "forms.inviteAccountMember.tip"}
          />
        </BodyCopy>
      </div>

      {emailFields.map((field, index) => (
        <IonGrid className={"ion-no-margin ion-no-padding " + styles.emailInputWrapper} key={field.id}>
          <IonRow className="ion-justify-content-center ion-align-items-center">
            <IonCol>
              <div>
                <Input<FormData>
                  data-test="email-input"
                  error={form.formState.errors.emails?.[index]?.email}
                  icon="envelope"
                  noSpacing
                  register={form.register(`emails.${index}.email`)}
                  type="email"
                />
              </div>
            </IonCol>
            <IonCol className={styles.keywordRemove} size="auto">
              <div className="ion-margin-start">
                <CircleButton
                  ariaLabelKey="accessibility.ariaLabels.delete"
                  icon="xmark"
                  onClick={() => emailRemove(index)}
                />
              </div>
            </IonCol>
          </IonRow>
        </IonGrid>
      ))}

      <Button fill="clear" onClick={() => emailAppend({ email: "" })}>
        <IconBase icon="add" slot="start" />
        <FormattedMessage id="forms.inviteAccountMember.addAnother" />
      </Button>

      <div className={consultScheduling ? "ion-hide" : ""}>
        <Input<FormData>
          error={form.formState.errors.message}
          label={intl.formatMessage({ id: "forms.inviteAccountMember.messageLabel" })}
          register={form.register("message")}
        />
      </div>

      <FormButtons
        expand={buttonExpand}
        hookForm={form}
        onCancel={onCancel}
        saveKey={saveKey}
        skipCancel={skipCancel}
      />
    </Form>
  );
};

export default InviteAccountMembersForm;
